/**
 * SessionServicesContext
 * 
 * Provides external services that session hooks need.
 * Each app (frontend, game) provides its own implementations via this context.
 * 
 * This allows hooks to remain app-agnostic while still accessing
 * API calls, auth, session tracking, etc.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { EmotionType } from '../types/emotion';
import type {
  AuthCredentials,
  CreateAuthHeaders,
  SessionTrackingCallbacks,
} from '@selna/session';

// ============================================================================
// Types for external services
// ============================================================================

/** Emotion analysis result */
export interface EmotionSequence {
  emotion: EmotionType;
  confidence: number;
  reasoning?: string;
}

/** Emotion analysis request */
export interface EmotionAnalysisRequest {
  lastAIMessage: string;
  fullTranscript: Array<{ speaker: 'user' | 'ai'; text: string; timestamp: number }>;
  scenarioContext: {
    title: string;
    description?: string;
    characterName?: string;
    category?: string;
    behavioralProfile?: any;
    positiveExpressionsOnly?: boolean;
  };
  currentEmotion?: EmotionType;
  emotionHistory?: EmotionType[];
}

/** Conversation log for API calls */
export interface ConversationLog {
  exchanges: Array<{
    speaker: string;
    message: string;
    timestamp?: string;
  }>;
  metadata: {
    scenarioId: string;
    startTime: string;
    endTime: string;
    userId: string;
  };
  sessionDuration: number;
  emotionHistory: Array<{ timestamp: number; emotion: EmotionType }>;
}

/** Scenario session data returned by service */
export interface ScenarioSessionData {
  success: boolean;
  sessionData: {
    instructions: string;
    model: string;
    voice: string;
  };
  scenario: {
    id: string;
    title: string;
    isGameMode?: boolean;
    description?: string;
    objectives?: string[];
    category?: string;
    industry?: string;
    difficulty?: string;
    estimatedTime?: string;
    firstSpeaker?: 'avatar' | 'user';
    languageMode?: 'user-preference' | 'english-only';
    characterName?: any;
    aiRole?: string;
    originalAiRole?: string;
    behavioralProfile?: any;
    analysisModeEnabled?: boolean;
    transcriptValidationEnabled?: boolean;
    translations?: any;
    [key: string]: any;
  };
  avatarImages: Record<string, string>;
  persona: any;
}

/** Session results when conversation ends */
export interface SessionResults {
  sessionId: string;
  scenarioId: string;
  userId: string;
  tenantId: string;
  startedAt: Date;
  endedAt: Date;
  duration: number;
  status: 'completed' | 'abandoned';
  transcript: Array<{ timestamp: number; speaker: 'user' | 'ai'; text: string }>;
  emotionHistory: Array<{ timestamp: number; emotion: EmotionType }>;
  guidedHelpStats?: any;
  scenario: { id: string; title: string; category: string };
  persona: any;
}

// AuthCredentials is imported from @selna/session above

// ============================================================================
// Service interfaces
// ============================================================================

export interface SessionServices {
  /** Analyze conversation for emotion detection */
  analyzeEmotions: (request: EmotionAnalysisRequest, credentials?: any) => Promise<EmotionSequence>;

  /** Request a guided hint from the backend */
  requestGuidedHint: (
    scenarioId: string,
    deploymentId: string | null,
    conversationLog: ConversationLog,
    emotionHistory: Array<{ timestamp: number; emotion: EmotionType }>,
    hintsShown: number,
    language: string,
    characterName?: string,
    lastAnalyzedExchangeIndex?: number,
    scenarioData?: any,
    previousHints?: any[],
    credentials?: any
  ) => Promise<any>;

  /** Verify if user followed a hint */
  verifyHintFollowed: (
    hintText: string,
    userMessage: string,
    pendingHints: any[],
    verifiedHints: any[],
    credentials?: any
  ) => Promise<any>;

  /** Request detailed help */
  requestDetailedHelp: (
    scenarioId: string,
    deploymentId: string | null,
    conversationLog: ConversationLog,
    emotionHistory: Array<{ timestamp: number; emotion: EmotionType }>,
    helpsUsed: number,
    language: string,
    characterName?: string,
    lastAnalyzedExchangeIndex?: number
  ) => Promise<any>;

  /** Validate transcript quality */
  validateTranscript: (
    scenarioId: string,
    scenarioTitle: string,
    exchanges: Array<{ speaker: string; message: string }>,
    language: string
  ) => Promise<any>;

  /** Create a session from scenario ID */
  createSession: (params: { scenarioId: string; userId?: string }) => Promise<ScenarioSessionData>;

  /** Preload avatar images with progress */
  preloadAvatarImages: (
    images: Record<string, string>,
    onProgress?: (loaded: number, total: number) => void
  ) => Promise<void>;

  /** Validate scenario data */
  validateScenarioData: (data: ScenarioSessionData) => void;

  /** Log a session tracking checkpoint */
  logCheckpoint: (checkpoint: string, sessionTrackingId: string) => void;

  /** Update current language */
  updateLanguage?: (language: any) => void;

  /** Get current language code (e.g., 'pt-PT') */
  getCurrentLanguage: () => string;

  /** Combine prompt with language instructions */
  combinePromptWithLanguage: (
    prompt: string,
    language: string,
    languageMode?: string,
    characterName?: any,
    includeAnalysisMode?: boolean,
    isDemoMode?: boolean
  ) => string;

  /** Get localized scenario with translations */
  getLocalizedScenario?: (scenario: any, language: string) => any;

  /** Update scenario fields via analysis mode API */
  analysisUpdateScenario: (
    scenarioId: string,
    updates: Record<string, any>,
    reasoning: string,
    regenerateUserReferenceInfo?: boolean
  ) => Promise<{ prompt: string; updatedFields: string[] }>;

  /** Auth credentials (optional, used by guided hints) */
  credentials?: any;

  /** WebSocket connection config (app provides these) */
  webSocketConfig: {
    apiBaseUrl: string;
    credentials: AuthCredentials | null;
    createAuthHeaders: CreateAuthHeaders;
    createSessionTracking: (sessionTrackingId?: string) => SessionTrackingCallbacks;
  };

  /** Config values */
  config: {
    realtimeModel: string;
    defaultVoice: string;
    analysisModeTools: any[];
    analysisModeEditTools?: any[];
    conversationEndTool?: any;
  };
}

// ============================================================================
// Context
// ============================================================================

const SessionServicesContext = createContext<SessionServices | null>(null);

/**
 * Hook to access session services from context.
 * Throws if used outside a provider.
 */
export function useSessionServices(): SessionServices {
  const services = useContext(SessionServicesContext);
  if (!services) {
    throw new Error(
      'useSessionServices must be used within a SessionServicesProvider. ' +
      'Wrap your session component with <SessionServicesProvider services={...}>.'
    );
  }
  return services;
}

/**
 * Provider component for session services.
 * Each app provides its own service implementations.
 */
export function SessionServicesProvider({
  services,
  children,
}: {
  services: SessionServices;
  children: ReactNode;
}) {
  return (
    <SessionServicesContext.Provider value={services}>
      {children}
    </SessionServicesContext.Provider>
  );
}

export default SessionServicesContext;
